import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { Spin, Empty } from 'antd';
import {
	EnvironmentOutlined,
	CarOutlined,
	ToolOutlined,
	EditOutlined,
	UpOutlined,
	DownOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import moment from 'moment';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import {
	WrenchModeEvent,
	ParsedLocationEvents,
	VisitSession,
	EVENT_LABELS,
	EMPTY_PARSED,
	formatEventTime,
	formatTimer,
	locationTimeZone,
	parseLocationEvents,
	partitionWorkOrders,
} from '../../utils/WrenchModeEventUtils';
import { workOrderSearchAndAPIAdaptor } from '../../thunks/work_orders_thunks_wrapper';

interface ScheduleWithEvents {
	schedule: any;
	events: WrenchModeEvent[];
	parsed: ParsedLocationEvents;
}

// A trip's [began, arrived] window (UTC ISO), used to block edits that would overlap
// another trip to the same site on the same day.
export interface DriveTripWindow {
	began: string;
	arrived: string;
}

// Everything the drive-time editor needs for one trip. Passed as a single object to onEditDrive
// and reused as the modal-open state in the page.
export interface DriveEditContext {
	beganEvent: WrenchModeEvent;
	arrivedEvent: WrenchModeEvent;
	location: any;
	pauseEvents: WrenchModeEvent[];
	otherTripWindows: DriveTripWindow[];
	scheduleDate: string;
}

type OnEditDrive = (context: DriveEditContext) => void;

interface WrenchModeEventTimelineProps {
	schedulesWithEvents: ScheduleWithEvents[];
	loading: boolean;
	onVisibleDateChange: (date: string) => void;
	scrollToTargetRef: React.MutableRefObject<{ date: string; locationId: number } | null>;
	scrollContainerRef: React.RefObject<HTMLElement>;
	locationIds?: number[];
	onEditServiceCall?: (
		workOrderId: number,
		serviceCallId: number,
		focus: 'checkIn' | 'checkOut'
	) => void;
	onEditDrive?: OnEditDrive;
}

// Everything an event row needs beyond the event itself. Threaded as one object rather than
// three positional args through renderVisitSession → renderBucket → renderEventRow.
interface EventRowContext {
	timeZone: string;
	// The day this card is filed under, so a row whose site-local date differs can say so.
	scheduleDate: string;
	onEditServiceCall?: (
		workOrderId: number,
		serviceCallId: number,
		focus: 'checkIn' | 'checkOut'
	) => void;
	completedServiceCallIds?: Set<number>;
}

// A visit can be in progress and still hold counted time — a session that banked hours before
// a pause, or an earlier closed session in the same visit, followed by a check-in with no
// checkout. The duration and the still-open state are independent facts, so neither is shown
// at the other's expense: the label alone when nothing is banked yet, the duration alone when
// the visit is closed, and both together when they are both true.
const formatTimerOrInProgress = (totalMs: number, inProgress: boolean): string => {
	if (!inProgress) return formatTimer(totalMs);
	if (totalMs <= 0) return 'In progress';
	return `${formatTimer(totalMs)} · In progress`;
};

const TIME_COL = 'bodyregular text-character-secondary';
const LABEL = 'bodyregular text-character-secondary';

const renderEditLink = (evt: WrenchModeEvent, context: EventRowContext): React.ReactNode => {
	const { onEditServiceCall, completedServiceCallIds } = context;
	if (!onEditServiceCall || !evt.workOrderId || !evt.serviceCallId) return null;
	if (completedServiceCallIds && !completedServiceCallIds.has(evt.serviceCallId)) return null;
	const focus = evt.eventType === 'started_work' ? 'checkIn' : 'checkOut';
	return (
		<AccessPermissionChecker name={PERMISSION_NAMES.REVISE_SERVICE_CALLS}>
			<a
				onClick={() =>
					onEditServiceCall(evt.workOrderId as number, evt.serviceCallId as number, focus)
				}
				aria-label="Edit"
				className="ml-2 shrink-0 text-character-secondary hover:text-character-secondary"
			>
				<EditOutlined />
			</a>
		</AccessPermissionChecker>
	);
};

// The drive-boundary events whose timestamps define a visit's drive time.
const findBeganTrip = (driveEvents: WrenchModeEvent[]): WrenchModeEvent | undefined =>
	driveEvents.find((evt) => evt.eventType === 'began_trip');
// Prefer `arrived`, falling back to `trip_cancelled` — the same preference the drive-time
// calc uses (parseLocationEvents: `driveEndEvent = arrived || tripCancelled`), so the pencil
// always edits the event that defines the displayed duration.
const findArrived = (driveEvents: WrenchModeEvent[]): WrenchModeEvent | undefined =>
	driveEvents.find((evt) => evt.eventType === 'arrived') ||
	driveEvents.find((evt) => evt.eventType === 'trip_cancelled');

const renderDriveEditLink = (
	driveEvents: WrenchModeEvent[],
	location: any,
	onEditDrive?: OnEditDrive,
	otherTripWindows: DriveTripWindow[] = [],
	scheduleDate = ''
): React.ReactNode => {
	if (!onEditDrive) return null;
	const beganEvent = findBeganTrip(driveEvents);
	const arrivedEvent = findArrived(driveEvents);
	// Drive time is the began_trip → arrived interval; only offer editing when both boundaries exist.
	if (!beganEvent || !arrivedEvent || beganEvent.id == null || arrivedEvent.id == null) return null;
	// Pauses are not editable, but the modal needs them to keep the edited window around them
	// and to show a pause-accurate preview.
	const pauseEvents = driveEvents.filter(
		(evt) => evt.eventType === 'trip_paused' || evt.eventType === 'trip_resumed'
	);
	return (
		<AccessPermissionChecker name={PERMISSION_NAMES.REVISE_SERVICE_CALLS}>
			<a
				onClick={() =>
					onEditDrive({
						beganEvent,
						arrivedEvent,
						location,
						pauseEvents,
						otherTripWindows,
						scheduleDate,
					})
				}
				aria-label="Edit drive time"
				className="ml-2 shrink-0 text-character-secondary hover:text-character-secondary"
			>
				<EditOutlined />
			</a>
		</AccessPermissionChecker>
	);
};

const renderEventLabel = (evt: WrenchModeEvent): React.ReactNode => {
	const baseLabel = EVENT_LABELS[evt.eventType] || evt.eventType;

	if (evt.eventType === 'started_work' && evt.workOrderId) {
		return (
			<span className={LABEL}>
				{baseLabel}:{' '}
				<Link
					to={'/supplier/workOrders/detail/' + evt.workOrderId}
					className="text-character-secondary underline hover:text-character-secondary"
				>
					WO #{evt.workOrderId}
				</Link>
			</span>
		);
	}

	if (evt.eventType === 'drive_note') {
		return (
			<div>
				<span className={LABEL}>{baseLabel}</span>
				{evt.note ? (
					<div className="footnoteregular mt-0.5 italic text-character-secondary">
						&ldquo;{evt.note}&rdquo;
					</div>
				) : null}
			</div>
		);
	}

	if (evt.eventType === 'ended_work' && evt.workOrderId) {
		const isIncomplete = evt.checkoutStatus === 'incomplete';
		return (
			<span className={LABEL}>
				{baseLabel}
				{isIncomplete ? ' \u2022 Incomplete' : ''}:{' '}
				<Link
					to={'/supplier/workOrders/detail/' + evt.workOrderId}
					className="text-character-secondary underline hover:text-character-secondary"
				>
					WO #{evt.workOrderId}
				</Link>
			</span>
		);
	}

	return <span className={LABEL}>{baseLabel}</span>;
};

const renderEventRow = (
	evt: WrenchModeEvent,
	key: React.Key,
	context: EventRowContext
): React.ReactNode => (
	<div className="flex items-start justify-between gap-2 px-m py-2" key={key}>
		<div className="flex min-w-0 flex-1 items-start gap-2">
			<span className={TIME_COL}>
				{formatEventTime(evt.createdAt, context.timeZone, context.scheduleDate)}
			</span>
			<div className="min-w-0 flex-1">{renderEventLabel(evt)}</div>
		</div>
		{renderEditLink(evt, context)}
	</div>
);

const renderBucket = (
	icon: React.ReactNode,
	label: string,
	total: string,
	events: WrenchModeEvent[],
	context: EventRowContext,
	headerAction?: React.ReactNode
): React.ReactNode => {
	if (events.length === 0) return null;
	return (
		<div>
			<div className="px-m py-xs">
				<div className="flex items-center gap-2 border-0 border-b border-solid border-grey-5">
					<span className="h5medium flex flex-1 items-center gap-2 text-grey-8">
						<span className="text-grey-8">{icon}</span>
						<span>{label}</span>
					</span>
					<span className="h5medium text-grey-10">{total}</span>
					{headerAction}
				</div>
			</div>
			<div className="">{events.map((evt, index) => renderEventRow(evt, index, context))}</div>
		</div>
	);
};

const flattenWorkEvents = (visit: VisitSession): WrenchModeEvent[] => {
	const out: WrenchModeEvent[] = [];
	for (const session of visit.workSessions) {
		for (const evt of session.events) {
			out.push(evt);
		}
	}
	return out;
};

const renderVisitSession = (
	visit: VisitSession,
	visitIndex: number,
	totalVisits: number,
	context: EventRowContext,
	location?: any,
	onEditDrive?: OnEditDrive,
	otherTripWindows: DriveTripWindow[] = [],
	scheduleDate = ''
): React.ReactNode => {
	const visitTotalMs = visit.driveTimeMs + visit.workTimeMs;
	const workEvents = flattenWorkEvents(visit);
	const workTimeLabel = formatTimerOrInProgress(visit.workTimeMs, !!visit.inProgress);
	return (
		<div key={visitIndex} className="flex flex-col gap-xs">
			{totalVisits > 1 ? (
				<div className="p-m">
					<div className="flex items-center justify-between gap-2 border-0 border-b border-solid border-grey-4 ">
						<span className="h5medium flex-1 text-grey-10">Session {visitIndex + 1}</span>
						<span className="h5medium text-grey-10">
							{formatTimerOrInProgress(visitTotalMs, !!visit.inProgress)}
						</span>
					</div>
				</div>
			) : null}
			{renderBucket(
				<CarOutlined />,
				'Drive Time',
				formatTimer(visit.driveTimeMs),
				visit.driveEvents,
				context,
				renderDriveEditLink(
					visit.driveEvents,
					location,
					onEditDrive,
					otherTripWindows,
					scheduleDate
				)
			)}
			{renderBucket(<ToolOutlined />, 'Work Time', workTimeLabel, workEvents, context)}
		</div>
	);
};

const filterLocationEvents = (events: WrenchModeEvent[], locationId: number): WrenchModeEvent[] => {
	const locStr = String(locationId);
	return events.filter((e) => String(e.locationId) === locStr);
};

interface LocationCardProps {
	cardKey: string;
	date: string;
	locationId: number;
	locationName: string;
	locationTotalMs: number;
	parsed: ParsedLocationEvents;
	hasVisits: boolean;
	hasAnyData: boolean;
	workOrders: any[];
	supplierEmail: string;
	scheduleDate: string;
	location: any;
	onEditServiceCall?: (
		workOrderId: number,
		serviceCallId: number,
		focus: 'checkIn' | 'checkOut'
	) => void;
	onEditDrive?: OnEditDrive;
	completedServiceCallIds: Set<number>;
}

const renderWoLinks = (workOrders: any[]) =>
	workOrders.map((wo: any, index: number) => (
		<span key={wo.id}>
			{index > 0 && ', '}
			<Link
				to={'/supplier/workOrders/detail/' + wo.id + '/details'}
				target="_blank"
				className="bodyregularunderline text-character-primary"
			>
				WO #{wo.id}
			</Link>
		</span>
	));

const LocationCard: React.FC<LocationCardProps> = ({
	cardKey,
	date,
	locationId,
	locationName,
	locationTotalMs,
	parsed,
	hasVisits,
	hasAnyData,
	workOrders,
	supplierEmail,
	scheduleDate,
	location,
	onEditServiceCall,
	onEditDrive,
	completedServiceCallIds,
}) => {
	const [woExpanded, setWoExpanded] = useState(false);

	// Site clock for every event row in this card.
	const eventRowContext: EventRowContext = {
		timeZone: locationTimeZone(location),
		scheduleDate,
		onEditServiceCall,
		completedServiceCallIds,
	};

	const hasWorkOrders = workOrders.length > 0;
	const partition = useMemo(() => {
		if (!hasWorkOrders) return null;
		return partitionWorkOrders(workOrders, supplierEmail, scheduleDate);
	}, [workOrders, supplierEmail, scheduleDate, hasWorkOrders]);

	const woCompleted = partition ? partition.ended.length : 0;
	const woTotal = workOrders.length;

	// Every trip to this site (one per visit session). Passed into each trip's editor as the
	// "other" windows so an edit can be blocked if it would overlap another trip the same day.
	const tripWindows = parsed.visitSessions
		.map((visit) => {
			const began = findBeganTrip(visit.driveEvents);
			const arrived = findArrived(visit.driveEvents);
			if (!began || !arrived || !began.createdAt || !arrived.createdAt) return null;
			return { beganId: began.id, began: began.createdAt, arrived: arrived.createdAt };
		})
		.filter(Boolean) as Array<{ beganId?: number; began: string; arrived: string }>;

	return (
		<div className="flex flex-col gap-2.5 px-3 py-4" data-date={date} data-location-id={locationId}>
			<div className="rounded bg-grey-3 px-3 py-4">
				<div className="flex items-center">
					<div className="flex flex-1 items-center gap-1">
						<EnvironmentOutlined className="text-grey-10" />
						<span className="bodyregular text-grey-10">{locationName}</span>
						{hasWorkOrders && (
							<>
								<ToolOutlined className="ml-1 text-grey-8" style={{ fontSize: 12 }} />
								<span
									className="bodyregular cursor-pointer text-grey-10"
									onClick={() => setWoExpanded(!woExpanded)}
								>
									{woCompleted}/{woTotal}{' '}
									{woExpanded ? (
										<UpOutlined style={{ fontSize: 10 }} />
									) : (
										<DownOutlined style={{ fontSize: 10 }} />
									)}
								</span>
							</>
						)}
					</div>
				</div>
				{woExpanded && partition && (
					<div className="-mx-3 mt-2 flex flex-col gap-xs bg-grey-5 px-3 py-2">
						{partition.notStarted.length > 0 && (
							<div className="bodyregular text-character-primary">
								Not Started: {renderWoLinks(partition.notStarted)}
							</div>
						)}
						{partition.started.length > 0 && (
							<div className="bodyregular text-character-primary">
								Started: {renderWoLinks(partition.started)}
							</div>
						)}
						{partition.ended.length > 0 && (
							<div className="bodyregular text-character-primary">
								Ended: {renderWoLinks(partition.ended)}
							</div>
						)}
					</div>
				)}
				<div className="mt-1 flex items-center justify-between">
					<div className="h5medium text-grey-10">Total Time</div>
					<span className="h4medium text-grey-10">
						{hasAnyData
							? formatTimerOrInProgress(
									locationTotalMs,
									parsed.visitSessions.some((visit) => visit.inProgress)
							  )
							: '--'}
					</span>
				</div>
			</div>
			<div className="flex flex-col bg-white">
				{hasVisits
					? parsed.visitSessions.map((visit, vIdx) => {
							const beganEvent = findBeganTrip(visit.driveEvents);
							const selfBeganId = beganEvent ? beganEvent.id : undefined;
							const otherTripWindows = tripWindows
								.filter((tripWindow) => tripWindow.beganId !== selfBeganId)
								.map((tripWindow) => ({ began: tripWindow.began, arrived: tripWindow.arrived }));
							return renderVisitSession(
								visit,
								vIdx,
								parsed.visitSessions.length,
								eventRowContext,
								location,
								onEditDrive,
								otherTripWindows,
								scheduleDate
							);
					  })
					: null}
				{!hasVisits && parsed.driveEvents.length > 0
					? renderBucket(
							<CarOutlined />,
							'Drive Time',
							formatTimer(parsed.driveTimeMs),
							parsed.driveEvents,
							eventRowContext,
							renderDriveEditLink(parsed.driveEvents, location, onEditDrive, [], scheduleDate)
					  )
					: null}
				{!hasAnyData ? (
					<div className="bodyregular px-4 py-2 text-character-secondary">
						No WrenchMode activity logged for this visit.
					</div>
				) : null}
			</div>
		</div>
	);
};

const WrenchModeEventTimeline: React.FC<WrenchModeEventTimelineProps> = ({
	schedulesWithEvents,
	loading,
	onVisibleDateChange,
	scrollToTargetRef,
	scrollContainerRef,
	locationIds,
	onEditServiceCall,
	onEditDrive,
}) => {
	const hasLocationFilter = locationIds && locationIds.length > 0;
	const innerRef = useRef<HTMLDivElement>(null);
	const suppressObserverRef = useRef<boolean>(false);
	const suppressTimeoutRef = useRef<number | null>(null);
	const debounceTimeoutRef = useRef<number | null>(null);
	const intersectingRef = useRef<Set<HTMLElement>>(new Set());

	// Service call ids that have both started_work and ended_work logged. Edit
	// link is suppressed for incomplete service calls (no checkout yet).
	const completedServiceCallIds = useMemo(() => {
		const started = new Set<number>();
		const ended = new Set<number>();
		for (const item of schedulesWithEvents) {
			for (const evt of item.events || []) {
				if (evt.serviceCallId == null) continue;
				if (evt.eventType === 'started_work') started.add(evt.serviceCallId);
				if (evt.eventType === 'ended_work') ended.add(evt.serviceCallId);
			}
		}
		const completed = new Set<number>();
		started.forEach((id) => {
			if (ended.has(id)) completed.add(id);
		});
		return completed;
	}, [schedulesWithEvents]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		const inner = innerRef.current;
		if (!container || !inner) return;

		const intersecting = intersectingRef.current;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const el = entry.target as HTMLElement;
					if (entry.isIntersecting) {
						intersecting.add(el);
					} else {
						intersecting.delete(el);
					}
				}

				if (suppressObserverRef.current) return;

				let topEl: HTMLElement | null = null;
				let topY = Infinity;
				intersecting.forEach((el) => {
					const rect = el.getBoundingClientRect();
					if (rect.top < topY) {
						topY = rect.top;
						topEl = el;
					}
				});

				if (!topEl) return;
				const date = (topEl as HTMLElement).getAttribute('data-date');
				if (!date) return;

				if (debounceTimeoutRef.current !== null) {
					window.clearTimeout(debounceTimeoutRef.current);
				}
				debounceTimeoutRef.current = window.setTimeout(() => {
					onVisibleDateChange(date);
				}, 100);
			},
			{
				root: container,
				rootMargin: '0px 0px -80% 0px',
				threshold: 0,
			}
		);

		const sections = inner.querySelectorAll('[data-date][data-location-id]');
		sections.forEach((section) => observer.observe(section));

		return () => {
			observer.disconnect();
			intersecting.clear();
			if (debounceTimeoutRef.current !== null) {
				window.clearTimeout(debounceTimeoutRef.current);
				debounceTimeoutRef.current = null;
			}
		};
	}, [schedulesWithEvents, onVisibleDateChange, scrollContainerRef]);

	const scrollToTarget = useCallback(
		(date: string, locationId: number) => {
			const inner = innerRef.current;
			const container = scrollContainerRef.current;
			if (!inner || !container) return;
			const el = inner.querySelector(
				'[data-date="' + date + '"][data-location-id="' + locationId + '"]'
			) as HTMLElement | null;
			if (!el) return;

			// Compute target scrollTop for the container only — avoids
			// scrollIntoView's default behavior of scrolling every scrollable
			// ancestor (including the page body).
			const elRect = el.getBoundingClientRect();
			const containerRect = container.getBoundingClientRect();
			const targetScrollTop = container.scrollTop + (elRect.top - containerRect.top);

			suppressObserverRef.current = true;
			if (suppressTimeoutRef.current !== null) {
				window.clearTimeout(suppressTimeoutRef.current);
			}
			suppressTimeoutRef.current = window.setTimeout(() => {
				suppressObserverRef.current = false;
			}, 700);

			container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
		},
		[scrollContainerRef]
	);

	useEffect(() => {
		const target = scrollToTargetRef.current;
		if (target) {
			scrollToTarget(target.date, target.locationId);
			scrollToTargetRef.current = null;
		}
	});

	if (loading && schedulesWithEvents.length === 0) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spin />
			</div>
		);
	}

	if (schedulesWithEvents.length === 0) {
		return (
			<div className="bg-white px-xs">
				<Empty description="No schedules for the selected date range" className="py-12" />
			</div>
		);
	}

	const dateGroups: Record<string, ScheduleWithEvents[]> = {};
	const dateOrder: string[] = [];
	for (const item of schedulesWithEvents) {
		const locations = (item.schedule && item.schedule.locations) || [];
		// Skip schedules with no locations — nothing to render for the day.
		if (locations.length === 0) continue;
		const date = item.schedule.date;
		if (!dateGroups[date]) {
			dateGroups[date] = [];
			dateOrder.push(date);
		}
		dateGroups[date].push(item);
	}

	return (
		<div ref={innerRef} className="bg-white">
			{dateOrder.map((date) => {
				const schedules = dateGroups[date];
				return (
					<div key={date} className="p-1">
						<div className="h4medium  bg-[#D7F0F6] p-4 text-center text-character-primary">
							{moment(date).format('MMMM D, YYYY')}
						</div>
						{schedules.map((item) => {
							const allLocations = item.schedule.locations || [];
							const locations = hasLocationFilter
								? allLocations.filter((loc: any) => locationIds.indexOf(loc.locationId) !== -1)
								: allLocations;
							return locations.map((loc: any, locIdx: number) => {
								const locationName = (loc.location && loc.location.name) || 'Unknown Location';
								const locationEvents = filterLocationEvents(item.events, loc.locationId);
								const parsed: ParsedLocationEvents =
									locationEvents.length > 0 ? parseLocationEvents(locationEvents) : EMPTY_PARSED;
								const locationTotalMs = parsed.driveTimeMs + parsed.totalWorkMs;
								const hasVisits = parsed.visitSessions.length > 0;
								const hasAnyData =
									hasVisits ||
									parsed.driveEvents.length > 0 ||
									parsed.workLogEvents.length > 0 ||
									parsed.timerEvents.length > 0;

								const rawWorkOrders = (loc && loc.workOrders) || [];
								const adaptedWorkOrders = workOrderSearchAndAPIAdaptor(
									rawWorkOrders.map((wo: any) => ({ source: wo }))
								);
								const hasWorkOrders = adaptedWorkOrders.length > 0;
								const cardKey = item.schedule.id + '-' + locIdx;

								return (
									<LocationCard
										key={cardKey}
										cardKey={cardKey}
										date={date}
										locationId={loc.locationId}
										locationName={locationName}
										locationTotalMs={locationTotalMs}
										parsed={parsed}
										hasVisits={hasVisits}
										hasAnyData={hasAnyData}
										workOrders={adaptedWorkOrders}
										supplierEmail={item.schedule.supplierEmail}
										scheduleDate={item.schedule.date}
										location={loc.location}
										onEditServiceCall={onEditServiceCall}
										onEditDrive={onEditDrive}
										completedServiceCallIds={completedServiceCallIds}
									/>
								);
							});
						})}
					</div>
				);
			})}
		</div>
	);
};

export default WrenchModeEventTimeline;
